import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { formatSeconds, isNumberZeroNullUndefined } from '@common/helpers';
import { FLIGHT_STATUS_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { AirportI18nAll } from '@common/interfaces/master/airport_i18n_all';
import { MasterDataS01P031 } from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportComponent } from '@lib/components/support-class';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { Bound, GetOrderResponseData, Type1 } from 'src/sdk-servicing';

@Component({
  selector: 'asw-trip-summary',
  templateUrl: './trip-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripSummaryComponent extends SupportComponent {
  id = this.constructor.name;
  bounds: Array<Bound> = [];

  private _subscriptions = new Subscription();
  private _airportI18nAll: Array<AirportI18nAll> = [];

  constructor(
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _staticMsg: StaticMsgPipe,
    private _change: ChangeDetectorRef,
    private _common: CommonLibService
  ) {
    super(_common);
  }

  init() {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? {};
        this.bounds = pnr.air?.bounds ?? [];
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result: MasterDataS01P031) => {
        this._airportI18nAll = result.airportI18nAll ?? [];
        this._change.markForCheck();
      })
    );
  }

  // (1.1.54) 乗継情報(セグメント)
  getConnection(
    flightIdx: number,
    flights: Array<Type1> = [],
    currentArrivalLocationCode: string = '',
    connectionTime?: number
  ): { isShow: boolean; isSameLocation: boolean } {
    const nextFlight = flights?.at(flightIdx + 1) ?? {};
    // 1) 当該セグメントのインデックス ≠ 当該バウンド.flights[]の件数-1の場合のみ表示する。
    const isShow = flightIdx !== (flights?.length ?? 0) - 1;
    /**
     * 1.1) 当該バウンド.flights[当該セグメントのインデックス].arrival.locationCode =
     *      当該バウンド.flights[当該セグメントのインデックス+1].departure.locationCodeの場合、青色。
     * 1.2) 当該バウンド.flights[当該セグメントのインデックス].arrival.locationCode ≠
     *      当該バウンド.flights[当該セグメントのインデックス+1].departure.locationCodeの場合、オレンジ色。
     */
    const isSameLocation = currentArrivalLocationCode === nextFlight.departure?.locationCode;
    return {
      isShow:
        isShow &&
        //4) 当該セグメント情報.connectionTimeが存在する、かつ以下のいずれかを満たす場合、以下のように表示する。
        !isNumberZeroNullUndefined(connectionTime),
      isSameLocation: isSameLocation,
    };
  }

  // (1.1.56) 乗継情報
  getLocationName(isSameLocation: boolean, locationCode: string = ''): string {
    /** 1)
     * 当該バウンド.flights[当該セグメントのインデックス].arrival.locationCode ≠
     * 当該バウンド.flights[当該セグメントのインデックス+1].departure.locationCodeの場合、
     * 別空港への移動を伴う乗継である旨の文言表示する。
     */
    if (!isSameLocation) return this._staticMsg.transform('label.interAirportTransit');
    /** 2)
     * 当該バウンド.flights[当該セグメントのインデックス].arrival.locationCode =
     * 当該バウンド.flights[当該セグメントのインデックス+1].departure.locationCodeの場合、
     * 当該セグメント情報.arrival.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、
     * 空港名称を取得して当該空港の名称を含む文言表示する。
     */
    const locationName =
      this._airportI18nAll.find((item: AirportI18nAll) => item?.airport_code === locationCode)?.airport_name ?? '';
    return this._staticMsg.transform('label.transitInAirport', { '0': locationName });
  }

  // (1.1.59) 乗継情報(バウンド間)
  isNextBound(index: number): boolean {
    // 当該バウンドのインデックス≠PNR情報取得API応答.data.air.bounds[]の件数-1の場合のみ表示する。
    return index !== this.bounds.length - 1;
  }

  isLastFlightCanceled(index: number) {
    return this.bounds.at(index)?.flights?.at(-1)?.statusCode === FLIGHT_STATUS_CODE.UN;
  }

  getFlightConnectionTime(connectionTime: number | undefined): string {
    return formatSeconds(connectionTime);
  }

  reload() {
    // Add comment to passed SonarLint
  }

  destroy() {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
